import type { EmailLog, SmsLog, SmsLogChange } from "../services/api";

export function formatLogTimestamp(
  date: string | Date | undefined | null
): string {
  if (date === undefined || date === null || date === "") {return "—";}
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {return String(date);}
  const formatted = d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return formatted.replace(",", "");
}

export function formatDisplayValue(value: unknown): string {
  if (value === undefined || value === null || value === "") {return "—";}
  if (Array.isArray(value)) {
    if (value.length === 0) {return "—";}
    return value.map(v => String(v)).join(", ");
  }
  return String(value);
}

export function formatTypeLabel(value?: string): string {
  if (!value) {return "—";}
  return value.replace(/_/g, " ");
}

export function isSuccessStatus(status?: string): boolean {
  const normalized = (status ?? "").toLowerCase();
  return ["sent", "delivered", "success", "verified"].includes(normalized);
}

export function isFailedStatus(status?: string): boolean {
  const normalized = (status ?? "").toLowerCase();
  return ["failed", "error", "bounced", "rejected", "deleted", "delete"].includes(
    normalized
  );
}

function isTruthyFlag(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }
  const s = String(value ?? "").toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

export function getHttpStatusLabel(code?: string | number): string {
  const num = Number(code);
  if (Number.isNaN(num)) {return "—";}
  if (num >= 200 && num < 300) {return "HTTP OK";}
  if (num >= 400 && num < 500) {return "Client Error";}
  if (num >= 500) {return "Server Error";}
  return "HTTP";
}

// SMS / OTP audit log field accessors
export function getSmsTimestamp(log: SmsLog): string | undefined {
  return (
    log.created ??
    log.verified_at ??
    log.sent_at ??
    log.created_at ??
    (log.timestamp as string | undefined)
  );
}

export function getSmsRecipient(log: SmsLog): string {
  return formatDisplayValue(
    log.phone ??
      log.phone_number ??
      log.recipient ??
      (typeof log.to === "string" ? log.to : undefined)
  );
}

export function getSmsTenantName(log: SmsLog): string {
  if (log.user) {
    return formatDisplayValue(log.user);
  }
  return formatDisplayValue(
    log.tenant_name ?? log.tenant ?? (log.tenantName as string | undefined)
  );
}

export function getSmsType(log: SmsLog): string {
  if (log.is_signup !== undefined && log.is_signup !== null && log.is_signup !== "") {
    return isTruthyFlag(log.is_signup) ? "Signup OTP" : "Login OTP";
  }
  const resource = String(log.resource ?? "");
  if (/loginotp/i.test(resource)) {
    return "Login OTP";
  }
  return formatDisplayValue(
    log.sms_type ?? log.type ?? (log.smsType as string | undefined)
  );
}

export function getSmsAction(log: SmsLog): string {
  return formatDisplayValue(log.action);
}

export function getSmsConfig(log: SmsLog): string {
  return formatDisplayValue(
    log.resource ??
      log.title ??
      log.config ??
      log.config_name ??
      (log.configName as string | undefined)
  );
}

export function getSmsStatus(log: SmsLog): string {
  if (log.status) {
    return formatDisplayValue(log.status);
  }
  if (log.verified_at) {
    return "verified";
  }
  const action = String(log.action ?? "").toLowerCase();
  if (action === "delete" || action === "deleted") {
    return "deleted";
  }
  if (action === "create") {
    return "created";
  }
  if (action) {
    return action;
  }
  return "pending";
}

export function getSmsTitle(log: SmsLog): string {
  return formatDisplayValue(log.title);
}

export function getSmsResource(log: SmsLog): string {
  return formatDisplayValue(log.resource);
}

export function getSmsUser(log: SmsLog): string {
  return formatDisplayValue(log.user);
}

export function getSmsOtp(log: SmsLog): string {
  if (log.otp === null || log.otp === undefined || log.otp === "") {
    return "—";
  }
  return String(log.otp);
}

export function getSmsChanges(log: SmsLog): SmsLogChange[] {
  return Array.isArray(log.changes) ? log.changes : [];
}

export function getSmsIpAddress(log: SmsLog): string {
  return formatDisplayValue(log.ip_address);
}

export function getSmsUserAgent(log: SmsLog): string {
  return formatDisplayValue(log.user_agent);
}

export function getSmsVerifiedAt(log: SmsLog): string | undefined {
  return log.verified_at ?? undefined;
}

export function getSmsIsSignup(log: SmsLog): string {
  if (log.is_signup === undefined || log.is_signup === null || log.is_signup === "") {
    return "—";
  }
  return isTruthyFlag(log.is_signup) ? "Yes" : "No";
}

export function getSmsObjectPk(log: SmsLog): string {
  return formatDisplayValue(log.object_pk);
}

export function getSmsCorrelationId(log: SmsLog): string {
  return formatDisplayValue(log.correlation_id);
}

export function getSmsMessage(log: SmsLog): string {
  return formatDisplayValue(log.message ?? log.body);
}

export function getSmsTenantId(log: SmsLog): string {
  return formatDisplayValue(log.tenant_id ?? (log.tenantId as string | undefined));
}

export function getSmsConfigId(log: SmsLog): string {
  return formatDisplayValue(log.config_id ?? (log.configId as string | undefined));
}

export function getSmsResponseContent(log: SmsLog): string {
  return formatDisplayValue(
    log.response_content ??
      log.gateway_response ??
      (log.response as string | undefined)
  );
}

export function getSmsStatusCode(log: SmsLog): string {
  return formatDisplayValue(
    log.status_code ?? log.http_status ?? (log.httpStatus as string | undefined)
  );
}

export function getSmsDebugUrl(log: SmsLog): string | undefined {
  const url = log.debug_url ?? (log.debugUrl as string | undefined);
  return url && String(url).trim() ? String(url) : undefined;
}

function formatEmailList(value?: string | string[] | null): string {
  if (value === undefined || value === null) {
    return "—";
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "—";
    }
    return value.map(v => String(v)).join(", ");
  }
  const s = String(value).trim();
  return s || "—";
}

export interface EmailRecipientRow {
  role: string;
  email: string;
}

// Email field accessors
export function getEmailSentAt(log: EmailLog): string | undefined {
  return (
    log.sent_at ??
    log.created_on ??
    log.delivered_at ??
    log.created_at ??
    (log.timestamp as string | undefined)
  );
}

export function getEmailSubject(log: EmailLog): string {
  return formatDisplayValue(log.subject ?? (log.message_title as string));
}

export function getEmailTenantName(log: EmailLog): string {
  return formatDisplayValue(
    log.tenant_name ??
      log.tenant ??
      log.tenant_id ??
      (log.tenantName as string | undefined)
  );
}

export function getEmailFrom(log: EmailLog): string {
  const name = log.from_name ?? (log.sender_name as string | undefined);
  const email =
    log.from_email ??
    log.from ??
    log.sender ??
    (log.sender_email as string | undefined);
  if (name && email) {return `${name} <${email}>`;}
  return formatDisplayValue(email ?? name);
}

export function getEmailTo(log: EmailLog): string {
  if (Array.isArray(log.to_emails) && log.to_emails.length > 0) {
    return formatEmailList(log.to_emails);
  }
  return formatDisplayValue(
    log.to_email ??
      log.recipient ??
      log.email ??
      (typeof log.to === "string" ? log.to : undefined)
  );
}

export function getEmailType(log: EmailLog): string {
  return formatDisplayValue(
    log.email_type ?? log.type ?? (log.emailType as string | undefined)
  );
}

export function getEmailStatus(log: EmailLog): string {
  return formatDisplayValue(log.status);
}

export function getEmailRecipientCount(log: EmailLog): string {
  const count =
    log.recipients_count ??
    log.recipient_count ??
    log.rcpt ??
    (log.recipients as number | undefined);
  return formatDisplayValue(count);
}

export function getEmailCc(log: EmailLog): string {
  if (Array.isArray(log.cc_emails)) {
    return formatEmailList(log.cc_emails);
  }
  return formatEmailList(log.cc ?? log.cc_list);
}

export function getEmailBcc(log: EmailLog): string {
  if (Array.isArray(log.bcc_emails)) {
    return formatEmailList(log.bcc_emails);
  }
  return formatEmailList(log.bcc ?? log.bcc_list);
}

export function getEmailReplyTo(log: EmailLog): string {
  return formatEmailList(log.reply_to ?? (log.replyTo as string | undefined));
}

export function getEmailRecipients(log: EmailLog): EmailRecipientRow[] {
  const rows: EmailRecipientRow[] = [];
  const toList = Array.isArray(log.to_emails) ? log.to_emails : [];
  const ccList = Array.isArray(log.cc_emails) ? log.cc_emails : [];
  const bccList = Array.isArray(log.bcc_emails) ? log.bcc_emails : [];

  toList.forEach(email => rows.push({ role: "To", email }));
  ccList.forEach(email => rows.push({ role: "CC", email }));
  bccList.forEach(email => rows.push({ role: "BCC", email }));

  if (rows.length === 0) {
    const fallback = getEmailTo(log);
    if (fallback !== "—") {
      rows.push({ role: "To", email: fallback });
    }
  }
  return rows;
}

export function getEmailExternalId(log: EmailLog): string {
  return formatDisplayValue(log.external_id ?? log.externalId);
}

export function getEmailPlainText(log: EmailLog): string {
  const text = log.plain_text ?? log.body ?? log.message ?? log.text_body;
  if (text === undefined || text === null || String(text).trim() === "") {
    return "—";
  }
  return String(text).trim();
}

export function getEmailHtmlBody(log: EmailLog): string {
  if (log.html_body === null || log.html_body === undefined || log.html_body === "") {
    return "—";
  }
  return String(log.html_body).trim();
}

export function getEmailErrorMessage(log: EmailLog): string {
  return formatDisplayValue(log.error_message);
}

export function getEmailTenantId(log: EmailLog): string {
  return formatDisplayValue(log.tenant_id ?? (log.tenantId as string | undefined));
}

export function getEmailCreatedBy(log: EmailLog): string {
  return formatDisplayValue(log.created_by);
}

export function getEmailUpdatedBy(log: EmailLog): string {
  return formatDisplayValue(log.updated_by);
}

export function getEmailDeliveredAt(log: EmailLog): string | undefined {
  return log.delivered_at ?? undefined;
}

export function getEmailCreatedAt(log: EmailLog): string | undefined {
  return log.created_on ?? log.created_at;
}

export function getEmailUpdatedAt(log: EmailLog): string | undefined {
  return log.updated_on ?? log.updated_at;
}
